import { useQuery } from '@tanstack/react-query'
import { DocumentActions } from '@/components/DocumentActions'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop'
import BoltIcon from '@mui/icons-material/Bolt'
import PaymentsIcon from '@mui/icons-material/Payments'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useState } from 'react'
import { api } from '@/api/client'
import { SendDocumentButton, SendDocumentIcon } from '@/components/SendDocument'
import { SendFeedbackRequestButton } from '@/components/SendFeedbackRequest'
import { FinalInvoiceDialog } from '@/pages/billing/FinalInvoiceDialog'
import { ProformaDialog } from '@/pages/billing/ProformaDialog'
import { RaiseRefundDialog } from '@/pages/billing/RaiseRefundDialog'
import { WriteOffDialog } from '@/pages/billing/WriteOffDialog'
import { MoveCustomerDialog } from '@/pages/orders/MoveCustomerDialog'
import { RecordPaymentDialog } from '@/pages/billing/RecordPaymentDialog'
import { GatePassDialog, type PassType } from '@/pages/gatepasses/GatePassDialog'
import { OrderEditDialog } from '@/pages/orders/OrderEditDialog'
import { PostponeDialog } from '@/pages/orders/PostponeDialog'
import { SessionEditDialog } from '@/pages/orders/SessionEditDialog'
import {
  ClosingDialog,
  CorrectionDialog,
  OpeningDialog,
} from '@/pages/utility/EbReadingDialogs'
import { DeliverySplitPanel } from '@/pages/orders/DeliverySplitPanel'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useMarkComplimentary,
  useAddManpowerRequirement,
  useAdjustManpowerRequirement,
  useOrderAmenitiesPending,
  useHalls,
  useOutlets,
  useManpowerRequirements,
  useOperationalSummary,
  useOrder,
  useOrderAction,
  useOrderCreditNotes,
  useOrderRefunds,
  useRefundable,
  useOrderEbReadings,
  useOrderGatePasses,
  useOrderPayments,
  usePaymentSchedule,
  useOrderInvoices,
  useForceOrderValue,
  useRecalculateOrderTotal,
  useDecideOrderDiscountApproval,
} from '@/api/hooks'
import type {
  EbReading,
  EventSession,
  GatePass,
  Invoice,
  ManpowerRequirement,
  OrderDetail,
  Payment,
} from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import {
  EmptyState,
  ErrorBanner,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatSlotHours,
  formatTimeOfDay,
  humanise,
  InlineSpinner,
  MetricCard,
  PageHeader,
  StatusChip,
} from '@/components/common'

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const hasRole = useAuthStore((state) => state.hasRole)
  const isAdmin = hasRole('SUPER_ADMIN') || hasRole('MANAGEMENT')
  const isSuperAdmin = hasRole('SUPER_ADMIN')
  const { data: order, isLoading, error } = useOrder(id)
  const { data: summary } = useOperationalSummary(id)
  const { data: schedule } = usePaymentSchedule(id)
  const { data: receipts } = useOrderPayments(id)
  const { data: manpower } = useManpowerRequirements(id)
  const action = useOrderAction()
  const markComplimentary = useMarkComplimentary()
  // What is genuinely owed back: paid, less what the order comes to, less any
  // refund already raised. The balance alone would offer the same money twice.
  const { data: refundable } = useRefundable(id)
  const { data: refunds } = useOrderRefunds(id)
  const { data: creditNotes } = useOrderCreditNotes(id)
  // What amenities add on top of the order's own stored total, before a
  // proforma or tax invoice has carried them into it - the hall's estimate
  // until a meter has been read, the metered figure once one has.
  const { data: amenitiesPending } = useOrderAmenitiesPending(id)

  const [tab, setTab] = useState('overview')
  const [invoiceDialog, setInvoiceDialog] = useState(false)
  const [proformaDialog, setProformaDialog] = useState(false)

  // What has already been raised against this order. A document that stands -
  // not cancelled, not superseded - is one to print rather than raise again.
  const { data: raised } = useOrderInvoices(id)
  const standing = (type: string) =>
    (raised ?? []).filter((one) => one.invoiceType === type && one.status !== 'CANCELLED')
  const proformas = standing('PROFORMA')
  const taxInvoices = standing('TAX_INVOICE')

  const print = (documents: Invoice[]) =>
    documents.forEach((one) => void api.print(`/api/v1/invoices/${one.id}/pdf`))

  // More than one document stands where a bill was split by tax rate - a
  // banquet's food and its hall rent, taxed differently, filed as two. Printing
  // both without asking hands over a bill nobody asked to see yet; this is
  // where the choice of which one (or both) is made instead.
  const [printChoice, setPrintChoice] = useState<Invoice[] | null>(null)
  const printOrAsk = (documents: Invoice[]) =>
    documents.length > 1 ? setPrintChoice(documents) : print(documents)

  const [refundDialog, setRefundDialog] = useState(false)
  const [writeOffDialog, setWriteOffDialog] = useState(false)
  const [paymentDialog, setPaymentDialog] = useState(false)
  const [editDialog, setEditDialog] = useState(false)
  const [postponeDialog, setPostponeDialog] = useState(false)
  const [movingSession, setMovingSession] = useState<EventSession | null>(null)
  const [editingReceipt, setEditingReceipt] = useState<Payment | null>(null)
  const [editingRequirement, setEditingRequirement] = useState<ManpowerRequirement | null>(null)
  const { data: outlets } = useOutlets()
  const { data: halls } = useHalls()
  const [cancelDialog, setCancelDialog] = useState(false)
  const [forceValueDialog, setForceValueDialog] = useState(false)
  const [recalculateTotalDialog, setRecalculateTotalDialog] = useState(false)
  const [movingCustomer, setMovingCustomer] = useState(false)

  if (isLoading) return <InlineSpinner />
  if (error) return <ErrorBanner error={error} />
  if (!order) return <EmptyState message="Order not found." />

  // Whether the event is held in one of our own spaces, which is what decides
  // the hall-side detail: a hall and slot to show against each sitting, a meter
  // to read, property to hand over. A job cooked at the customer's address has
  // none of it. Read off what the sittings actually book rather than off the
  // service type, because a restaurant party sits in our dining room too.
  const onOurPremises = order.sessions.some((session) => session.hallId)
  const cancelled = order.status === 'CANCELLED'
  // Outdoor catering and bulk takeaway travel; a banquet and a restaurant
  // party are served where they are booked.
  const delivers = ['OUTDOOR_CATERING', 'BULK_TAKEAWAY'].includes(order.serviceType)

  /**
   * Whether this kind of job is served here at all.
   *
   * <p>Not the same question as whether a hall has been chosen yet. A banquet
   * with no hall on it is a banquet that still needs one, and the overview
   * should say so rather than going quiet about halls altogether.</p>
   */
  const servedHere = !delivers


  /** The branch this was taken at, named rather than left as an identifier. */
  const outletName = (outlets ?? []).find((one) => one.id === order.outletId)?.name

  /**
   * The halls its sittings are in, and the slots they hold.
   *
   * <p>Usually one hall - it is chosen once for the event - but a long day can
   * take the morning in one and the evening in another, and a sitting can hold
   * two slots of the same hall. So it is a line per sitting rather than a
   * single answer, and the slot hours are named because that is what the hall
   * is actually let for.</p>
   */
  const held = order.sessions
    .filter((session) => session.hallId)
    .map((session) => {
      const hall = (halls ?? []).find((one) => one.id === session.hallId)
      const slotIds = session.hallSlotIds?.length
        ? session.hallSlotIds
        : session.hallSlotId
          ? [session.hallSlotId]
          : []
      const slots = slotIds
        .map((id) => hall?.timeSlots?.find((slot) => slot.id === id))
        .filter((slot): slot is NonNullable<typeof slot> => Boolean(slot))
        .sort((left, right) => left.startTime.localeCompare(right.startTime))
        .map((slot) => `${slot.name} ${formatSlotHours(slot.startTime, slot.endTime)}`)
      return {
        id: session.id,
        // Kept whole, so the line can be opened and moved from here rather
        // than sending somebody to another tab to find the same sitting.
        session,
        // Named only where there is more than one; on a single sitting the
        // name is the order's own occasion said twice.
        sitting: session.eventName ?? 'Session',
        hall: hall?.name ?? 'Hall',
        slots,
        // A hall taken without a named slot is still taken, and for hours
        // somebody has to know. Naming the hall and stopping there says the
        // room and not the afternoon.
        hours: `${formatTimeOfDay(session.eventStart)}–${formatTimeOfDay(session.eventEnd)}`,
      }
    })

  /**
   * Where the food is going, written the way it would be read out to a driver.
   *
   * <p>Taken off the first sitting that carries one: a job that travels is
   * quoted to an address, and the sittings of one day go to the same place.</p>
   */
  const deliveryLines = (() => {
    const session = order.sessions.find((one) => one.address || one.deliveryAddress)
    if (!session) return [] as string[]
    const where = session.address
    const parts = where
      ? [
          // The door and the building on one line, the way it is said aloud.
          [where.doorNo, where.buildingName, where.floorNo].filter(Boolean).join(', '),
          where.streetName,
          [where.areaName, where.postalCode].filter(Boolean).join(' '),
          where.landmark ? `Near ${where.landmark}` : '',
        ]
      : [session.deliveryAddress ?? '']
    const lines = parts.filter((line): line is string => Boolean(line && line.trim()))
    if (session.venueDistanceKm) {
      lines.push(`${session.venueDistanceKm} km from the kitchen`)
    }
    return lines
  })()

  return (
    <>
      <PageHeader
        title={order.orderNumber}
        subtitle={`${order.customerName} · ${humanise(order.eventType)} on ${formatDate(order.eventDate)}`}
        actions={
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
              flexWrap: 'wrap',
              rowGap: 1,
              // One size everywhere in this row - some of these buttons carry
              // an icon and some don't, and a row of unequal heights reads as
              // broken even before it runs out of width and wraps.
              '& .MuiButtonBase-root': { fontSize: '0.8125rem', minHeight: 32, py: 0.5 },
              '& .MuiButton-startIcon > *:first-of-type': { fontSize: '1.1rem' },
            }}
          >
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orders')}>
              Back
            </Button>
            {/* The same act as revising a quotation: reopens the whole order
                pre-filled and saves it wholesale, past the change-request
                desk rather than through it. Once the final invoice stands,
                that document carries the figures as they were - an outlet
                user reads from here on, and a further edit is an
                administrator's call, same as the server itself refuses. */}
            {canWrite('order') && !['CANCELLED', 'CLOSED'].includes(order.status)
              && (isAdmin || taxInvoices.length === 0) && (
              <Button
                startIcon={<EditOutlinedIcon />}
                onClick={() => navigate(`/orders/${order.id}/revise`)}
              >
                Edit
              </Button>
            )}
            {/* Moving the whole booking to another day, every sitting at once
                and its own hall checked free there first - not the "Event
                date" box in Edit, which only ever primes what a session
                added through that wizard starts out as. */}
            {canWrite('order') && !['CANCELLED', 'CLOSED'].includes(order.status) && (
              <Button
                startIcon={<EventRepeatIcon />}
                onClick={() => setPostponeDialog(true)}
              >
                Change date
              </Button>
            )}
            <Button
              startIcon={<LocalPrintshopIcon />}
              onClick={() => void api.print(`/api/v1/orders/${order.id}/confirmation-pdf`)}
            >
              Print confirmation
            </Button>
            <SendDocumentButton kind="order-confirmation" id={order.id} label="Send…" size="medium" />
            {/* Not only when something is owed. Money is taken at the counter
                before the order has been priced at all, and that deposit sits
                unallocated until there is a bill to put it against. */}
            {canWrite('payment') && !['CANCELLED', 'CLOSED'].includes(order.status) && (
              <Button
                variant="contained"
                startIcon={<PaymentsIcon />}
                onClick={() => setPaymentDialog(true)}
              >
                {order.status === 'AWAITING_ADVANCE' ? 'Collect advance' : 'Collect payment'}
              </Button>
            )}
            {/* Money received already confirms a draft or an awaiting-advance
                order on its own once enough is in - this is for one that
                reached that point some other way and was left sitting
                unconfirmed regardless. */}
            {can('order.confirm') && ['DRAFT', 'AWAITING_ADVANCE'].includes(order.status) && (
              <Button
                variant="outlined"
                onClick={() => action.mutate({ id: order.id, action: 'confirm', body: {} })}
              >
                {order.status === 'AWAITING_ADVANCE' ? 'Confirm without advance' : 'Confirm'}
              </Button>
            )}
            {/* The kitchen's own sheet, not the customer's document. Reachable
                while the food is still to go out, and afterwards, because a
                copy gets asked for when something is queried. */}
            <Button
              startIcon={<SoupKitchenIcon />}
              onClick={() => navigate(`/orders/${order.id}/kitchen-slip`)}
            >
              Kitchen slip
            </Button>
            {/* The kitchen is told what to cook when an order is confirmed. An
                order waiting on its advance has told nobody, and the day it is
                served comes whether the advance did or not - so the list can be
                drawn from the menu as it stands. Drawn again, where one already
                exists, for dishes added since. */}
            {canWrite('order') && order.menuItems.length > 0
              && !['DRAFT', 'CANCELLED', 'CLOSED'].includes(order.status) && (
              <Button
                variant="outlined"
                startIcon={<SoupKitchenIcon />}
                disabled={action.isPending}
                onClick={() => action.mutate({ id: order.id, action: 'kitchen-requests', body: {} })}
              >
                Send to kitchen
              </Button>
            )}
            {/* Whatever stage the order is at, short of cancelled. What may
                actually be billed is the server's to say - it refuses a tax
                invoice before the event and asks for a proforma first - and
                hiding the button taught nobody why. */}
            {canWrite('billing') && !['CANCELLED', 'CLOSED'].includes(order.status) && (
              <>
                {/* The proforma comes first: it is what the customer approves.
                    Once it exists the same button prints it, because that is
                    what anybody comes back to this screen for - raising a
                    second one when the first is sitting there is how an order
                    ends up with three live estimates on it. */}
                <Button
                  variant="outlined"
                  onClick={() => (proformas.length > 0 ? printOrAsk(proformas) : setProformaDialog(true))}
                >
                  {proformas.length > 0 ? 'Print proforma' : 'Raise proforma'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => (taxInvoices.length > 0 ? printOrAsk(taxInvoices) : setInvoiceDialog(true))}
                >
                  {taxInvoices.length > 0 ? 'Print final invoice' : 'Raise final invoice'}
                </Button>
              </>
            )}
            {canWrite('feedback') && order.status !== 'CANCELLED' && (
              <SendFeedbackRequestButton orderId={order.id} />
            )}
            {/* Given away, or charged for after all. Whether it may still be
                changed is the server's to say: once a bill has gone out the
                kind of document is settled, and it refuses. */}
            {canWrite('billing') && !['CANCELLED', 'CLOSED'].includes(order.status) && (
              <Button
                variant="outlined"
                startIcon={<CardGiftcardIcon />}
                onClick={() =>
                  markComplimentary.mutate({ id: order.id, complimentary: !order.complimentary })
                }
                disabled={markComplimentary.isPending}
              >
                {order.complimentary ? 'Charge for it' : 'Complimentary'}
              </Button>
            )}
            {/* Money in beyond what the order comes to is owed back, and this
                is where somebody is looking when they notice. */}
            {/* A balance nobody is going to collect. Let go on the record,
                with a signature, rather than left to sit in the outstanding
                report for ever. */}
            {canWrite('billing') && order.balanceAmount > 0
              && ['COMPLETED', 'BILLED', 'CLOSED'].includes(order.status) && (
              <Button variant="outlined" onClick={() => setWriteOffDialog(true)}>
                Write off {formatMoney(order.balanceAmount)}
              </Button>
            )}
            {canWrite('payment') && (refundable?.refundable ?? 0) > 0 && (
              <Button variant="outlined" onClick={() => setRefundDialog(true)}>
                Refund {formatMoney(refundable?.refundable ?? 0)}
              </Button>
            )}
            {/* Before a final invoice stands, every route to the total is
                still open - this just retraces them rather than naming a
                figure outright. Once one stands, Force value is the tool;
                the two are opposites and never both offered at once. */}
            {isSuperAdmin && taxInvoices.length === 0
              && !['CANCELLED', 'CLOSED'].includes(order.status) && (
              <Button variant="outlined" onClick={() => setRecalculateTotalDialog(true)}>
                Recalculate total
              </Button>
            )}
            {/* For once a final invoice stands and every other route to the
                total - a package's rate, a guest count, an extra item - is
                refused. Before that, correcting whatever is actually wrong
                is the way in; this button is not a substitute for it, so it
                is not offered until there is something past it to fix. */}
            {isSuperAdmin && taxInvoices.length > 0
              && !['CANCELLED', 'CLOSED'].includes(order.status) && (
              <Button variant="outlined" onClick={() => setForceValueDialog(true)}>
                Force value
              </Button>
            )}
            {can('order.cancel') && !['CANCELLED', 'CLOSED'].includes(order.status) && (
              <Button color="error" onClick={() => setCancelDialog(true)}>
                Cancel
              </Button>
            )}
          </Stack>
        }
      />

      <ErrorBanner error={action.error} />

      {order.status === 'AWAITING_ADVANCE' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Awaiting a booking advance of {formatMoney(order.advanceRequired)}. The hall is held but not
          confirmed until it is received — recording the advance confirms the order automatically.
        </Alert>
      )}
      {action.isPending && <LinearProgress sx={{ mb: 2 }} />}

      {/* Given away, and said on the screen rather than left to be worked out
          from a bill nobody chased. The tick is here and not in the edit form
          because it decides what kind of document the bill will be, which is
          settled once and cannot be changed after one has gone out. */}
      {order.complimentary && (
        <Alert severity="info" icon={<CardGiftcardIcon fontSize="small" />} sx={{ mb: 2 }}>
          This booking is complimentary. It is priced out as it stands, so what it came to is on
          record, and the bill raised on it asks for nothing.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <MetricCard label="Status" value={<StatusChip status={order.status} size="medium" />} />
        </Grid>
        <Grid item xs={6} md={3}>
          {/* A cancelled booking owes the cancellation charge and nothing else.
              Calling that "total" beside the value of an event nobody will cook
              is what made the screen read as a bill for the whole wedding. */}
          <MetricCard
            label={cancelled ? 'Cancellation charge' : 'Total'}
            value={formatMoney(order.totalAmount + (cancelled ? 0 : amenitiesPending ?? 0))}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label="Paid" value={formatMoney(order.paidAmount)} tone="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          {/* Paid beyond what is owed is money to give back, not a debt of less
              than nothing, and the card says which of the two it is. */}
          {(order.refundableAmount ?? 0) > 0 ? (
            <MetricCard
              label="To refund"
              value={formatMoney(order.refundableAmount)}
              tone="warning"
            />
          ) : (
            <MetricCard
              label="Balance"
              value={formatMoney(order.balanceAmount + (cancelled ? 0 : amenitiesPending ?? 0))}
              tone={order.balanceAmount + (cancelled ? 0 : amenitiesPending ?? 0) > 0 ? 'warning' : 'success'}
            />
          )}
        </Grid>
      </Grid>

      {summary && summary.outstandingActions.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Still outstanding for this event
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {summary.outstandingActions.map((item) => (
              <li key={item}>
                <Typography variant="body2">{item}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      <Card>
        <Tabs value={tab} onChange={(_event, value) => setTab(value)} variant="scrollable">
          <Tab label="Overview" value="overview" />
          <Tab label="Package &amp; items" value="menu" />
          <Tab label="Operations" value="operations" />
          {/* Only where the food travels: a banquet is served where it is
              booked and has nothing to split. */}
          {delivers ? <Tab label="Parcels" value="parcels" /> : null}
          <Tab label="Gate passes" value="gate-passes" />
          {/* Metering is a banquet matter: a job cooked at the customer's
              address burns their electricity, not ours. */}
          {onOurPremises ? <Tab label="EB readings" value="eb-readings" /> : null}
          <Tab label="Payments" value="payments" />
          <Tab label="History" value="history" />
        </Tabs>
        <Divider />
        <CardContent>
          {tab === 'overview' && (
            <Grid container spacing={2}>
              {/* Who it is for and what was taken, before what it comes to.
                  Somebody opening an order is answering a question about an
                  event - which day, which branch, which hall - and used to have
                  to piece that together from three tabs. */}
              <Field
                label="Customer"
                value={
                  <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                    <span>{order.customerName}</span>
                    {order.contactPersonName ? (
                      <Typography variant="caption" color="text.secondary">
                        {order.contactPersonName}
                        {order.contactPhone ? ` · ${order.contactPhone}` : ''}
                      </Typography>
                    ) : null}
                    {/* A booking taken against a namesake is corrected by moving
                        the order, not by renaming the customer: the name on the
                        record is on all of their other orders too. */}
                    {canWrite('order') && !['CANCELLED', 'CLOSED'].includes(order.status) ? (
                      <Link
                        component="button"
                        type="button"
                        variant="caption"
                        underline="hover"
                        onClick={() => setMovingCustomer(true)}
                      >
                        Change
                      </Link>
                    ) : null}
                  </Stack>
                }
              />
              <Field label="Event date" value={formatDate(order.eventDate)} />
              <Field label="Event type" value={humanise(order.eventType)} />
              <Field label="Service type" value={humanise(order.serviceType)} />
              <Field label="Outlet" value={outletName ?? '—'} />
              <Field label="Guests" value={`${formatNumber(order.guestCount)} (${order.childGuestCount} children)`} />
              {/* Where it is being served. On our premises that is a hall out
                  of the diary; off it, it is an address the crew has to find,
                  and neither is any use on the other kind of job. */}
              {/* Wherever there is a hall, and wherever this kind of job would
                  have one. Keying it on the line of business alone hid a hall
                  that had actually been chosen on a job that travels - and a
                  hall somebody booked is a hall somebody booked. */}
              {held.length > 0 || servedHere ? (
                <Field
                  label={held.length > 1 ? 'Halls and slots' : 'Hall and slot'}
                  span={held.length > 1 ? 12 : 6}
                  value={
                    held.length === 0 ? (
                      'Not chosen yet'
                    ) : (
                      <Stack spacing={0.25}>
                        {held.map((one) => (
                          <Stack
                            key={one.id}
                            direction="row"
                            spacing={1}
                            alignItems="baseline"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <span>
                              {held.length > 1 ? `${one.sitting} · ` : ''}
                              {one.hall}
                              {' · '}
                              {one.slots.length > 0 ? one.slots.join(' · ') : one.hours}
                            </span>
                            {/* Opens the sitting itself, where the slot board
                                shows what else is taken that day and the hall
                                is re-checked against everyone else. */}
                            {canWrite('order') && !cancelled ? (
                              <Link
                                component="button"
                                type="button"
                                variant="caption"
                                underline="hover"
                                onClick={() => setMovingSession(one.session)}
                              >
                                Change hall or slot
                              </Link>
                            ) : null}
                          </Stack>
                        ))}
                      </Stack>
                    )
                  }
                />
              ) : null}
              {/* The same the other way: an address given on a banquet is an
                  address somebody wrote down for a reason. */}
              {deliveryLines.length > 0 || delivers ? (
                <Field
                  label="Delivery address"
                  span={12}
                  value={
                    deliveryLines.length > 0 ? (
                      <Stack spacing={0.5}>
                        {deliveryLines.map((line) => (
                          <span key={line}>{line}</span>
                        ))}
                      </Stack>
                    ) : (
                      'Not given yet'
                    )
                  }
                />
              ) : null}
              <Field label="Contracted amount" value={formatMoney(order.contractedAmount)} />
              {/* Only where something was given away, with the name beside it -
                  the question at a month end is never the amount alone. */}
              {order.discountAmount ? (
                <Field
                  label="Discount"
                  value={`${formatMoney(order.discountAmount)}${
                    order.discountPercent ? ` (${order.discountPercent}%)` : ''
                  } — recommended by ${
                    order.discountRecommendedBy || 'not recorded'
                  }${order.discountReason ? ` (${order.discountReason})` : ''}`}
                  span={12}
                />
              ) : null}
              {order.pendingDiscountAmount ? (
                <Grid item xs={12}>
                  <PendingOrderDiscount order={order} />
                </Grid>
              ) : null}
              {order.recoveryAmount > 0 && (
                <Field
                  label="Post-event recoveries"
                  value={formatMoney(order.recoveryAmount)}
                />
              )}
              <Field label="Advance required" value={formatMoney(order.advanceRequired)} />
              <Field label="Confirmed at" value={formatDateTime(order.confirmedAt)} />
              <Field label="Completed at" value={formatDateTime(order.completedAt)} />
              <Field
                label="Closure"
                value={`Operations ${order.operationsClosed ? 'signed off' : 'pending'} · Finance ${
                  order.financeClosed ? 'signed off' : 'pending'
                }`}
                span={12}
              />
              <Field label="Cancellation terms" value={order.cancellationTerms ?? '—'} span={12} />
              {/* What the office wrote on the order - and, for an order carried
                  over from ECEEBILL, the old bill line by line. */}
              {order.notes ? (
                <Field
                  label="Notes"
                  value={
                    <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-line' }}>
                      {order.notes}
                    </Typography>
                  }
                  span={12}
                />
              ) : null}
            </Grid>
          )}

          {/* One page. What each sitting eats and what is charged on top of
              it are the same conversation with a customer, and were two tabs
              nobody could see at once. */}
          {tab === 'menu' && (
            <>
              <PendingChanges order={order} />
              <OrderMenuBySession order={order} />
            </>
          )}

          {tab === 'gate-passes' && <OrderGatePasses order={order} />}

          {tab === 'eb-readings' && <OrderEbReadings order={order} />}

          {tab === 'parcels' && <DeliverySplitPanel order={order} />}
          {tab === 'operations' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  Manpower requirements
                </Typography>
                {canWrite('manpower') && !['CANCELLED', 'CLOSED'].includes(order.status) ? (
                  <AddDesignation orderId={order.id} />
                ) : null}
                {!manpower || manpower.length === 0 ? (
                  <EmptyState message="Nobody is on this job yet. Add a designation above." />
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Role</TableCell>
                        <TableCell align="right">Rule</TableCell>
                        <TableCell align="right">Required</TableCell>
                        <TableCell align="right">Allocated</TableCell>
                        <TableCell align="right">Shortage</TableCell>
                        <TableCell align="right">Estimated cost</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {manpower.map((requirement) => (
                        <TableRow key={requirement.id} hover>
                          <TableCell>{humanise(requirement.staffRole)}</TableCell>
                          <TableCell align="right">{requirement.ruleDerivedCount}</TableCell>
                          <TableCell align="right">{requirement.requiredCount}</TableCell>
                          <TableCell align="right">{requirement.allocatedCount}</TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              color={requirement.shortage > 0 ? 'error.main' : 'success.main'}
                            >
                              {requirement.shortage}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{formatMoney(requirement.estimatedCost)}</TableCell>
                          <TableCell align="right">
                            {canWrite('manpower') && !['CANCELLED', 'CLOSED'].includes(order.status) ? (
                              <Tooltip title="Change the headcount">
                                <IconButton
                                  size="small"
                                  aria-label={`Edit ${humanise(requirement.staffRole)}`}
                                  onClick={() => setEditingRequirement(requirement)}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Grid>
            </Grid>
          )}

          {tab === 'payments' && (
            <>
            {/* What actually came in, before what was promised. A milestone
                schedule is a plan; a receipt is money, and the office looking
                for a wrong cheque number is looking for the receipt. */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Receipts
            </Typography>
            {!receipts || receipts.length === 0 ? (
              <EmptyState message="Nothing has been collected against this order yet." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Receipt</TableCell>
                    <TableCell>Taken</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Unallocated</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.map((receipt) => (
                    <TableRow key={receipt.id} hover>
                      <TableCell>{receipt.receiptNumber}</TableCell>
                      <TableCell>{formatDate(receipt.paymentDate)}</TableCell>
                      <TableCell>{humanise(receipt.paymentMode)}</TableCell>
                      <TableCell align="right">{formatMoney(receipt.amount)}</TableCell>
                      <TableCell align="right">
                        {receipt.unallocatedAmount > 0
                          ? formatMoney(receipt.unallocatedAmount)
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={receipt.reversed ? 'REVERSED' : receipt.status} />
                      </TableCell>
                      <TableCell align="right">
                        {can('payment.edit') && !receipt.reversed ? (
                          <Tooltip title="Correct the details of this receipt">
                            <IconButton
                              size="small"
                              aria-label={`Edit ${receipt.receiptNumber}`}
                              onClick={() => setEditingReceipt(receipt)}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <DocumentActions
                          path={`/api/v1/payments/${receipt.id}/pdf`}
                          fileName={`${receipt.receiptNumber.replace(/\//g, '-')}.pdf`}
                          label="receipt"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
              Milestones
            </Typography>
            {!schedule || schedule.length === 0 ? (
              <EmptyState message="No payment milestones yet." />
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Milestone</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell align="right">Amount due</TableCell>
                    <TableCell align="right">Paid</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schedule.map((milestone) => (
                    <TableRow key={milestone.id} hover>
                      <TableCell>{humanise(milestone.milestone)}</TableCell>
                      <TableCell>{formatDate(milestone.dueDate)}</TableCell>
                      <TableCell align="right">{formatMoney(milestone.amountDue)}</TableCell>
                      <TableCell align="right">{formatMoney(milestone.amountPaid)}</TableCell>
                      <TableCell align="right">{formatMoney(milestone.outstanding)}</TableCell>
                      <TableCell>
                        <StatusChip status={milestone.status} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Money the business decided not to collect, with the voucher
                beside it: a balance that vanishes from a report with no paper
                behind it is the thing an audit asks about. */}
            {creditNotes && creditNotes.length > 0 ? (
              <>
                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                  Discounts, rebates and write-offs
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Voucher</TableCell>
                      <TableCell>Raised</TableCell>
                      <TableCell>What</TableCell>
                      <TableCell>Why</TableCell>
                      <TableCell align="right">Value</TableCell>
                      <TableCell align="right">Tax</TableCell>
                      <TableCell align="right">Off the balance</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {creditNotes.map((note) => (
                      <TableRow key={note.id} hover>
                        <TableCell>{note.creditNoteNumber}</TableCell>
                        <TableCell>{formatDate(note.noteDate)}</TableCell>
                        <TableCell>{humanise(note.noteType)}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                          <Typography variant="body2">{note.reason}</Typography>
                        </TableCell>
                        <TableCell align="right">{formatMoney(note.taxableAmount)}</TableCell>
                        <TableCell align="right">{formatMoney(note.taxAmount)}</TableCell>
                        <TableCell align="right">{formatMoney(note.totalAmount)}</TableCell>
                        <TableCell>
                          <StatusChip status={note.status} />
                        </TableCell>
                        <TableCell align="right">
                          <DocumentActions
                            path={`/api/v1/credit-notes/${note.id}/pdf`}
                            fileName={`${note.creditNoteNumber.replace(/\//g, '-')}.pdf`}
                            label="voucher"
                          />
                          <SendDocumentIcon kind="write-off-voucher" id={note.id} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : null}

            {/* Money going the other way, with the document beside it. Somebody
                who has just refunded a customer is looking at the order, not at
                the finance screens. */}
            {refunds && refunds.length > 0 ? (
              <>
                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                  Refunds
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Voucher</TableCell>
                      <TableCell>Raised</TableCell>
                      <TableCell>Why</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Withheld</TableCell>
                      <TableCell align="right">To pay</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {refunds.map((refund) => (
                      <TableRow key={refund.id} hover>
                        <TableCell>{refund.refundNumber}</TableCell>
                        <TableCell>{formatDate(refund.refundDate)}</TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                          <Typography variant="body2">{refund.reason}</Typography>
                          {refund.reversalReason ? (
                            <Typography variant="caption" color="error">
                              Reversed: {refund.reversalReason}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">{formatMoney(refund.amount)}</TableCell>
                        <TableCell align="right">{formatMoney(refund.deductionAmount)}</TableCell>
                        <TableCell align="right">{formatMoney(refund.netPayable)}</TableCell>
                        <TableCell>
                          <StatusChip status={refund.status} />
                        </TableCell>
                        <TableCell align="right">
                          <DocumentActions
                            path={`/api/v1/refunds/${refund.id}/pdf`}
                            fileName={`${refund.refundNumber.replace(/\//g, '-')}.pdf`}
                            label="refund voucher"
                          />
                          <SendDocumentIcon kind="refund-voucher" id={refund.id} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : null}
            </>
          )}

          {tab === 'history' && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.statusHistory.map((entry, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{formatDateTime(entry.changedAt)}</TableCell>
                    <TableCell>{humanise(entry.fromStatus)}</TableCell>
                    <TableCell>
                      <StatusChip status={entry.toStatus} />
                    </TableCell>
                    <TableCell>{entry.remarks ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CancelDialog open={cancelDialog} orderId={order.id} onClose={() => setCancelDialog(false)} />
      <ForceValueDialog
        order={forceValueDialog ? order : null}
        onClose={() => setForceValueDialog(false)}
      />
      <RecalculateTotalDialog
        order={recalculateTotalDialog ? order : null}
        onClose={() => setRecalculateTotalDialog(false)}
      />
      <MoveCustomerDialog
        order={movingCustomer ? order : null}
        onClose={() => setMovingCustomer(false)}
      />
      <RecordPaymentDialog
        open={paymentDialog}
        order={order}
        onClose={() => setPaymentDialog(false)}
      />
      <RecordPaymentDialog
        open={Boolean(editingReceipt)}
        payment={editingReceipt}
        onClose={() => setEditingReceipt(null)}
      />
      <ProformaDialog
        order={proformaDialog ? order : null}
        onClose={() => setProformaDialog(false)}
      />
      <FinalInvoiceDialog
        order={invoiceDialog ? order : null}
        onClose={() => setInvoiceDialog(false)}
      />
      <Dialog open={Boolean(printChoice)} onClose={() => setPrintChoice(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Which document?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This order's bill was split across more than one tax rate. Print one of them, or both.
          </Typography>
          <Stack spacing={1}>
            {(printChoice ?? []).map((one) => (
              <Button
                key={one.id}
                variant="outlined"
                startIcon={<LocalPrintshopIcon />}
                sx={{ justifyContent: 'space-between' }}
                onClick={() => {
                  print([one])
                  setPrintChoice(null)
                }}
              >
                <span>{one.invoiceNumber}</span>
                <span>{formatMoney(one.totalAmount)}</span>
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintChoice(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (printChoice) print(printChoice)
              setPrintChoice(null)
            }}
          >
            Print both
          </Button>
        </DialogActions>
      </Dialog>
      <RaiseRefundDialog
        open={refundDialog}
        fixedOrderId={order.id}
        onClose={() => setRefundDialog(false)}
      />
      <WriteOffDialog
        open={writeOffDialog}
        fixedOrderId={order.id}
        onClose={() => setWriteOffDialog(false)}
      />
      <OrderEditDialog order={order} open={editDialog} onClose={() => setEditDialog(false)} />
      <PostponeDialog
        order={postponeDialog ? order : null}
        onClose={() => setPostponeDialog(false)}
      />
      <SessionEditDialog
        order={order}
        session={movingSession}
        onClose={() => setMovingSession(null)}
      />
      <ManpowerRequirementDialog
        orderId={order.id}
        requirement={editingRequirement}
        onClose={() => setEditingRequirement(null)}
      />
    </>
  )
}

/**
 * What the kitchen cooks, read sitting by sitting.
 *
 * <p>A dish belongs to a sitting, not to the order as a whole - the reception
 * and the dinner that follows it are cooked and served separately - so the menu
 * is grouped the way it is worked. Each sitting carries the detail that used to
 * live on its own tab: when it runs, how many it seats, and where.</p>
 */
function OrderMenuBySession({ order }: { order: OrderDetail }) {
  const bySession = new Map<string, OrderDetail['menuItems']>()
  for (const item of order.menuItems) {
    const key = item.orderEventId ?? ''
    const existing = bySession.get(key)
    if (existing) existing.push(item)
    else bySession.set(key, [item])
  }
  // Dishes carrying no sitting: ordered before sittings existed, or meant for
  // the whole day. They are still the kitchen's to cook, so they are shown.
  const loose = bySession.get('') ?? []


  return (
    <>
      {order.sessions.length === 0 ? (
        <Stack spacing={1} alignItems="center" sx={{ py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No session on this order yet. Use Edit, above, to add one.
          </Typography>
        </Stack>
      ) : null}

      {order.sessions.map((session) => {
        return (
          <Box key={session.id} sx={{ mb: 3 }}>
            {/* Which sitting, when it runs, and how it is served. The hall,
                the head count and the address are the event rather than the
                menu, and they are on the overview - repeated here they pushed
                the dishes off the bottom of the screen. */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Typography variant="subtitle2">
                {session.sequenceNo}. {session.eventName ?? 'Session'}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={`${formatDateTime(session.eventStart)} – ${formatDateTime(session.eventEnd)}`}
              />
              {session.serviceStyle ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={session.serviceStyle === 'LEAF' ? 'Leaf service' : 'Buffet'}
                />
              ) : null}
            </Stack>

            {/* Told apart, because they are not the same thing on a bill. What
                came off the package is inside the rate a head; what was asked
                for on top of it is charged on top of it, and an office
                checking an invoice needs to see which is which without
                counting. */}
            <MenuLines
              items={(bySession.get(session.id) ?? []).filter((item) => !item.extra)}
              heading="From the package"
              empty="Nothing off the package on this session yet."
              minimal
            />

            <MenuLines
              items={(bySession.get(session.id) ?? []).filter((item) => item.extra)}
              heading="Chosen separately"
              empty=""
            />
          </Box>
        )
      })}

      {loose.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Not tied to a session
          </Typography>
          <MenuLines items={loose} empty="" />
        </Box>
      ) : null}
    </>
  )
}

function MenuLines({
  items,
  heading,
  empty,
  minimal,
}: {
  items: OrderDetail['menuItems']
  /** Says which of the two lists this is; omitted where there is only one. */
  heading?: string
  empty: string
  /** Package lines are fixed by the package definition — just the name, no
      quantity, unit or type to show against them. */
  minimal?: boolean
}) {
  if (items.length === 0) {
    return empty ? (
      <Typography variant="body2" color="text.secondary">
        {empty}
      </Typography>
    ) : null
  }
  return (
    <>
    {heading ? (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        {heading}
      </Typography>
    ) : null}
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Item</TableCell>
          {minimal ? null : (
            <>
              <TableCell align="right">Quantity</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Type</TableCell>
            </>
          )}
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.id} hover>
            <TableCell>
              {item.itemName}
              {/* Says which dishes the counter is for, without a column that
                  would be blank on most events. */}
              {item.liveCooking ? (
                <Chip size="small" color="secondary" label="Live" sx={{ ml: 1 }} />
              ) : null}
            </TableCell>
            {minimal ? null : (
              <>
                <TableCell align="right">{formatNumber(item.quantity)}</TableCell>
                <TableCell>{humanise(item.unit)}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={item.extra ? 'Extra' : 'In package'} />
                </TableCell>
              </>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
    </>
  )
}

/**
 * Changes the headcount a requirement asks for.
 *
 * <p>The rules derive a number from the guest count; the office knows the event.
 * The reason is kept with the change because a variance above the configured
 * threshold goes for approval, and whoever approves it has to see why more was
 * asked for.</p>
 */
function ManpowerRequirementDialog({
  orderId,
  requirement,
  onClose,
}: {
  orderId: string
  requirement: ManpowerRequirement | null
  onClose: () => void
}) {
  const adjust = useAdjustManpowerRequirement()
  const [count, setCount] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!requirement) return
    setCount(String(requirement.requiredCount))
    setReason('')
  }, [requirement])

  if (!requirement) return null

  const derived = requirement.ruleDerivedCount
  const wanted = Number(count)
  const variance = Number.isFinite(wanted) ? wanted - derived : 0
  const valid = count !== '' && wanted >= 0

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{humanise(requirement.staffRole)}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={adjust.error} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The rules derived {derived} for this event. {requirement.allocatedCount} allocated so far.
        </Typography>
        <TextField
          fullWidth
          type="number"
          label="Required"
          value={count}
          onChange={(event) => setCount(event.target.value)}
          inputProps={{ min: 0 }}
        />
        <TextField
          fullWidth
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why this differs from the rule"
          sx={{ mt: 2 }}
        />
        {variance !== 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {variance > 0 ? `${variance} more` : `${Math.abs(variance)} fewer`} than the rule derived. A
            large variance goes for approval before it takes effect.
          </Alert>
        ) : null}
        {wanted < requirement.allocatedCount ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {requirement.allocatedCount} are already allocated. Cutting below that leaves people rostered
            against a requirement that no longer asks for them.
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!valid || adjust.isPending}
          onClick={() =>
            adjust.mutate(
              { requirementId: requirement.id, orderId, requiredCount: wanted, reason: reason || undefined },
              { onSuccess: onClose },
            )
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/**
 * Putting a designation on the job: a name and how many.
 *
 * <p>The rules derive what follows from the covers and the counters. They know
 * nothing about the cleaner somebody wants for a hall the customer is fussy
 * about, or the second driver for a venue an hour away - and a headcount the
 * office cannot state is one it keeps on paper instead.</p>
 */
function AddDesignation({ orderId }: { orderId: string }) {
  const add = useAddManpowerRequirement()
  const [role, setRole] = useState('')
  const [count, setCount] = useState('1')

  const valid = role.trim() !== '' && Number(count) > 0

  const submit = () => {
    if (!valid) return
    add.mutate(
      { orderId, staffRole: role.trim(), requiredCount: Number(count) },
      {
        onSuccess: () => {
          setRole('')
          setCount('1')
        },
      },
    )
  }

  return (
    <>
      <ErrorBanner error={add.error} />
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Designation"
          placeholder="Waiter, cleaner, driver…"
          value={role}
          onChange={(event) => setRole(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              submit()
            }
          }}
          sx={{ width: 260 }}
        />
        <TextField
          size="small"
          type="number"
          label="How many"
          value={count}
          onChange={(event) => setCount(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              submit()
            }
          }}
          inputProps={{ min: 1, 'aria-label': 'How many' }}
          sx={{ width: 130 }}
        />
        <Button variant="outlined" disabled={!valid || add.isPending} onClick={submit}>
          Add
        </Button>
      </Stack>
    </>
  )
}

/**
 * Changes to the menu waiting on the customer.
 *
 * <p>Adding or taking off a dish once the order is agreed does not happen on
 * our say-so, so it sits here until they answer. Shown on the same page as the
 * menu because otherwise the menu looks wrong: somebody adds a dish, does not
 * see it, and adds it again.</p>
 */
function PendingChanges({ order }: { order: OrderDetail }) {
  const waiting = (order.changeRequests ?? []).filter(
    (change) => change.customerStatus === 'PENDING' && !change.applied,
  )
  const [copied, setCopied] = useState<string | null>(null)

  if (waiting.length === 0) return null

  const linkFor = (token: string) => `${window.location.origin}/order-change/${token}`

  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Waiting on the customer
      </Typography>
      <Stack spacing={1}>
        {waiting.map((change) => (
          <Stack
            key={change.id}
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="body2">
              {change.reason ?? change.changeNumber}
              {change.additionalAmount > 0 ? ` · ${formatMoney(change.additionalAmount)}` : ''}
            </Typography>
            {change.approvalToken ? (
              <Button
                size="small"
                onClick={() => {
                  // The office resends the link by hand; a customer who lost
                  // the message should not have to be raised a second change.
                  void navigator.clipboard.writeText(linkFor(change.approvalToken as string))
                  setCopied(change.id)
                }}
              >
                {copied === change.id ? 'Link copied' : 'Copy the link'}
              </Button>
            ) : null}
          </Stack>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        The menu changes when they agree, and not before.
      </Typography>
    </Alert>
  )
}

/**
 * The meter readings taken for this booking, and the entries that record them.
 *
 * <p>Only for an event held in one of our own halls: the consumption recovered
 * on the final invoice is what that hall's meter turned while the guest had
 * it.</p>
 */
function OrderEbReadings({ order }: { order: OrderDetail }) {
  const canWrite = useAuthStore((state) => state.canWrite)
  const can = useAuthStore((state) => state.can)
  const hasRole = useAuthStore((state) => state.hasRole)
  const isAdmin = hasRole('SUPER_ADMIN') || hasRole('MANAGEMENT')
  const { data: readings, error } = useOrderEbReadings(order.id)
  const [opening, setOpening] = useState(false)
  // Every still-open reading at once, so a hall wired with several meters is
  // closed in the one form rather than one dialog per board.
  const [closing, setClosing] = useState<EbReading[]>([])
  const [correctingReading, setCorrectingReading] = useState<EbReading | null>(null)

  const hallId = order.sessions.find((session) => session.hallId)?.hallId
  const writable = canWrite('utility') || canWrite('order')
  const stillOpen = (readings ?? []).filter((reading) => reading.closingReading == null)

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <div>
          <Typography variant="subtitle2">EB readings</Typography>
          <Typography variant="caption" color="text.secondary">
            Opening and closing meter entries for this booking, priced at the hall tariff
          </Typography>
        </div>
        {writable && order.status !== 'CANCELLED' ? (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<BoltIcon />} onClick={() => setOpening(true)}>
              Opening entry
            </Button>
            {/* Every meter still open on this booking, in the one form - a
                hall wired with several boards is closed once, not per meter. */}
            {stillOpen.length > 0 ? (
              <Button size="small" variant="outlined" onClick={() => setClosing(stillOpen)}>
                Closing entr{stillOpen.length === 1 ? 'y' : 'ies'} ({stillOpen.length})
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      <ErrorBanner error={error} />

      {!readings || readings.length === 0 ? (
        <EmptyState message="No meter reading taken for this booking yet." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Hall / meter</TableCell>
              <TableCell align="right">Opening</TableCell>
              <TableCell align="right">Closing</TableCell>
              <TableCell align="right">Units</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {readings.map((reading) => (
              <TableRow key={reading.id} hover>
                <TableCell>{reading.readingNumber}</TableCell>
                <TableCell>
                  {reading.hallName ?? '—'}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {reading.meterNumber ?? 'meter not named'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {formatNumber(reading.openingReading)}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {formatDateTime(reading.openingAt)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {reading.closingReading == null ? '—' : formatNumber(reading.closingReading)}
                </TableCell>
                <TableCell align="right">{formatNumber(reading.unitsConsumed)}</TableCell>
                <TableCell align="right">{formatMoney(reading.amount)}</TableCell>
                <TableCell>
                  <StatusChip status={reading.status} />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {writable && reading.closingReading == null ? (
                      <Button size="small" onClick={() => setClosing([reading])}>
                        Closing entry
                      </Button>
                    ) : null}
                    {(reading.status !== 'BILLED' || isAdmin) && can('utility.edit') ? (
                      <Tooltip
                        title={
                          reading.status === 'BILLED'
                            ? 'Already invoiced — correcting it fixes the record, not the invoice'
                            : 'Correct this reading'
                        }
                      >
                        <IconButton
                          size="small"
                          aria-label={`Edit ${reading.readingNumber}`}
                          onClick={() => setCorrectingReading(reading)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <OpeningDialog
        open={opening}
        order={{
          id: order.id,
          orderNumber: order.orderNumber,
          customerName: order.customerName,
          hallId,
        }}
        onClose={() => setOpening(false)}
      />
      <ClosingDialog readings={closing} onClose={() => setClosing([])} />
      <CorrectionDialog
        reading={correctingReading}
        onClose={() => setCorrectingReading(null)}
      />
    </>
  )
}

/**
 * The gate passes raised against this order, and the button that raises one.
 *
 * <p>Both kinds of pass hang off an order - vessels going out to an outdoor job,
 * hall property handed to a guest for the night - so the order is where they are
 * read. Which kind is offered follows the service type: an outdoor job has no
 * hall to hand over, and a banquet sends nothing out.</p>
 */
function OrderGatePasses({ order }: { order: OrderDetail }) {
  const can = useAuthStore((state) => state.can)
  const canWrite = useAuthStore((state) => state.canWrite)
  const { data: passes, error } = useOrderGatePasses(order.id)
  const [raising, setRaising] = useState<PassType | null>(null)
  const [editingPass, setEditingPass] = useState<GatePass | null>(null)

  // Which pass is offered follows what the order actually holds, not its
  // service type: a party sat in our own dining room hands hall property over
  // just as a banquet does, while a job cooked at the customer's address has no
  // hall to hand over and everything it needs has to travel there.
  const hallId = order.sessions.find((session) => session.hallId)?.hallId
  const outdoor = hallId === undefined
  const defaultType: PassType = outdoor ? 'OUTDOOR_VESSEL' : 'HALL_ASSET'
  const writable = can('equipment.issue') || canWrite('equipment')

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1, mb: 1 }}>
        <div>
          <Typography variant="subtitle2">Gate passes</Typography>
          <Typography variant="caption" color="text.secondary">
            {outdoor
              ? 'Vessels and equipment sent out for this job, and what came back'
              : 'Hall property handed over for this event, and what was collected again'}
          </Typography>
        </div>
        {writable && order.status !== 'CANCELLED' ? (
          <Button size="small" variant="outlined" onClick={() => setRaising(defaultType)}>
            {outdoor ? 'Raise outpass' : 'Raise handover'}
          </Button>
        ) : null}
      </Stack>

      <ErrorBanner error={error} />

      {!passes || passes.length === 0 ? (
        <EmptyState message="No gate pass raised against this order." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Number</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Issued</TableCell>
              <TableCell>Due back</TableCell>
              <TableCell align="right">Out</TableCell>
              <TableCell align="right">Pending</TableCell>
              <TableCell align="right">Recovery</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {passes.map((pass) => (
              <TableRow key={pass.id} hover>
                <TableCell>{pass.issueNumber}</TableCell>
                <TableCell>
                  {pass.issueType === 'OUTDOOR_VESSEL' ? 'Vessel outpass' : 'Hall handover'}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {pass.issueType === 'OUTDOOR_VESSEL'
                      ? pass.vehicleNumber ?? pass.driverName ?? ''
                      : pass.hallName ?? pass.issuedToName ?? ''}
                  </Typography>
                </TableCell>
                <TableCell>{formatDateTime(pass.issuedAt)}</TableCell>
                <TableCell>{formatDateTime(pass.expectedReturnAt)}</TableCell>
                <TableCell align="right">{formatNumber(pass.totalIssuedQuantity)}</TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    color={pass.totalPendingQuantity > 0 ? 'warning.main' : 'success.main'}
                  >
                    {formatNumber(pass.totalPendingQuantity)}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatMoney(pass.totalRecoveryAmount)}</TableCell>
                <TableCell>
                  <StatusChip status={pass.status} />
                </TableCell>
                <TableCell align="right">
                  {/* Only while it is still a draft: once it has gone out,
                      what left the store is a fact, and the way to change the
                      figures is to record what came back. */}
                  {writable && pass.status === 'DRAFT' ? (
                    <Tooltip title="Correct this draft">
                      <IconButton
                        size="small"
                        aria-label={`Edit ${pass.issueNumber}`}
                        onClick={() => setEditingPass(pass)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <DocumentActions
                    path={`/api/v1/gate-passes/${pass.id}/pdf`}
                    fileName={`${pass.issueNumber.replace(/\//g, '-')}.pdf`}
                    label="pass"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <GatePassDialog
        open={editingPass !== null}
        type={editingPass?.issueType ?? defaultType}
        pass={editingPass}
        onClose={() => setEditingPass(null)}
      />
      <GatePassDialog
        open={raising !== null}
        type={raising ?? defaultType}
        order={{
          id: order.id,
          orderNumber: order.orderNumber,
          customerName: order.customerName,
          outletId: order.outletId,
          hallId,
        }}
        onClose={() => setRaising(null)}
      />
    </>
  )
}

/**
 * A discount parked beyond the preparer's own authority, shown with a way
 * through it rather than left as a dead end - what the request was for, and
 * the same approve or turn down a manager would otherwise reach from
 * Settings &gt; Approvals.
 */
function PendingOrderDiscount({ order }: { order: OrderDetail }) {
  const decide = useDecideOrderDiscountApproval()
  if (!order.pendingDiscountAmount) return null

  return (
    <Alert
      severity="warning"
      action={
        <Stack direction="row" spacing={1}>
          <Button
            color="inherit"
            size="small"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ orderId: order.id, approved: false })}
          >
            Turn down
          </Button>
          <Button
            color="inherit"
            size="small"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ orderId: order.id, approved: true })}
          >
            Approve
          </Button>
        </Stack>
      }
    >
      <ErrorBanner error={decide.error} />
      A discount of {formatMoney(order.pendingDiscountAmount)} ({order.pendingDiscountPercent}%) is
      waiting on approval
      {order.pendingDiscountReason ? `: ${order.pendingDiscountReason}` : ''}
      {order.pendingDiscountRecommendedBy ? ` — recommended by ${order.pendingDiscountRecommendedBy}` : ''}
    </Alert>
  )
}

function Field({ label, value, span = 6 }: { label: string; value: React.ReactNode; span?: number }) {
  return (
    <Grid item xs={12} sm={span} md={span === 12 ? 12 : 4}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {value}
      </Typography>
    </Grid>
  )
}


function CancelDialog({
  open,
  orderId,
  onClose,
}: {
  open: boolean
  orderId: string
  onClose: () => void
}) {
  const action = useOrderAction()
  const [reason, setReason] = useState('')
  // Blank means "whatever the policy says". Typed into, it is the office
  // deciding otherwise, and the figure typed is the one charged.
  const [override, setOverride] = useState('')

  // What the policy makes of today's notice, read when the dialog opens so the
  // office sees the figure before it commits to it rather than after.
  const { data: recommended, isLoading } = useQuery({
    queryKey: ['cancellation-charge', orderId] as const,
    queryFn: () =>
      api.get<{
        charge: number
        percentOfOrder: number
        daysNotice: number
        orderTotal: number
      }>(`/api/v1/orders/${orderId}/cancellation-charge`),
    enabled: open,
  })

  const typed = Number(override)
  const overridden = override.trim() !== '' && Number.isFinite(typed) && typed >= 0

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel this order</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={action.error} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          The hall, kitchen, staff and vehicle commitments are released. What the customer owes is
          worked out from the cancellation policy and the notice given.
        </Alert>

        {isLoading ? (
          <InlineSpinner />
        ) : recommended ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>{formatMoney(recommended.charge)}</strong> at{' '}
              {recommended.percentOfOrder}% of {formatMoney(recommended.orderTotal)}, with{' '}
              {recommended.daysNotice} day{recommended.daysNotice === 1 ? '' : 's'} notice.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Billed as a cancellation charge at 18% GST, like any other service.
            </Typography>
          </Alert>
        ) : null}

        <TextField
          label="Charge instead"
          value={override}
          onChange={(event) => setOverride(event.target.value)}
          type="number"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          helperText={
            overridden
              ? `The customer will be charged ${formatMoney(typed)} rather than what the policy says`
              : 'Leave blank to charge what the policy says'
          }
        />

        <TextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          multiline
          minRows={3}
          fullWidth
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Keep the order</Button>
        <Button
          color="error"
          variant="contained"
          disabled={action.isPending || !reason.trim()}
          onClick={async () => {
            await action.mutateAsync({
              id: orderId,
              action: 'cancel',
              body: { reason, overrideCharge: overridden ? typed : undefined },
            })
            onClose()
          }}
        >
          Cancel the order
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ForceValueDialog({
  order,
  onClose,
}: {
  order: OrderDetail | null
  onClose: () => void
}) {
  const forceValue = useForceOrderValue()
  const [totalAmount, setTotalAmount] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (order) {
      setTotalAmount(String(order.totalAmount))
      setReason('')
      forceValue.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order])

  if (!order) return null
  const typed = Number(totalAmount)
  const valid = totalAmount.trim() !== '' && Number.isFinite(typed) && typed >= 0

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Force {order.orderNumber}'s value</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={forceValue.error} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          Sets the order's total directly, past its package rate, guest count and extra items. Those
          stay as they are - only the total moves, through the change amount. The standing final
          invoice is corrected to match, unless it was split across more than one tax rate, in which
          case it is left alone for a credit note.
        </Alert>
        <TextField
          label="Order total (Rs.)"
          value={totalAmount}
          onChange={(event) => setTotalAmount(event.target.value)}
          type="number"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          helperText={`Currently ${formatMoney(order.totalAmount)}`}
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          multiline
          minRows={3}
          fullWidth
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={forceValue.isPending || !valid || !reason.trim()}
          onClick={async () => {
            await forceValue.mutateAsync({ orderId: order.id, totalAmount: typed, reason })
            onClose()
          }}
        >
          Force it
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function RecalculateTotalDialog({
  order,
  onClose,
}: {
  order: OrderDetail | null
  onClose: () => void
}) {
  const recalculate = useRecalculateOrderTotal()

  useEffect(() => {
    if (order) {
      recalculate.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order])

  if (!order) return null

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Recalculate {order.orderNumber}'s total</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={recalculate.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          Reads every session's package and extra items as they stand today and moves the
          order's total to agree with them, grossed the same way the confirmation and proforma
          already read them - through the change amount, the same as any other correction. For a
          total left behind by an old pricing bug, not for anything that is still wrong today.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Currently {formatMoney(order.totalAmount)}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={recalculate.isPending}
          onClick={async () => {
            await recalculate.mutateAsync(order.id)
            onClose()
          }}
        >
          Recalculate
        </Button>
      </DialogActions>
    </Dialog>
  )
}
